import { create } from 'zustand';
import {
  TransformTool,
  ViewportState,
  BoxSelectionState,
  DEFAULT_VIEWPORT_STATE,
} from '../../core/types/scene.types';

interface SceneViewStore {
  // Viewport
  viewport: ViewportState;
  setZoom: (zoom: number) => void;
  setPan: (x: number, y: number) => void;
  resetView: () => void;

  // Tools
  activeTool: TransformTool;
  setActiveTool: (tool: TransformTool) => void;

  // Grid & Snap
  toggleGrid: () => void;
  toggleSnap: () => void;
  toggleGizmos: () => void;
  toggleRulers: () => void;
  setGridSize: (size: number) => void;

  // Box Selection
  boxSelection: BoxSelectionState | null;
  startBoxSelection: (x: number, y: number) => void;
  updateBoxSelection: (x: number, y: number) => void;
  endBoxSelection: () => void;

  // Mouse state
  mousePosition: { x: number; y: number };
  setMousePosition: (x: number, y: number) => void;

  // Transform state
  isTransforming: boolean;
  setIsTransforming: (value: boolean) => void;

  // Project state methods
  loadProjectState: (state: {
    panX: number;
    panY: number;
    zoom: number;
    activeTool?: string;
    showGrid?: boolean;
    showRulers?: boolean;
    snapToGrid?: boolean;
    gridSize?: number;
  }) => void;
  getProjectState: () => {
    panX: number;
    panY: number;
    zoom: number;
    activeTool: string;
    showGrid: boolean;
    showRulers: boolean;
    snapToGrid: boolean;
    gridSize: number;
  };
  resetToDefaults: () => void;
}

export const useSceneViewStore = create<SceneViewStore>()((set, get) => ({
  // Viewport
  viewport: { ...DEFAULT_VIEWPORT_STATE },

  setZoom: (zoom) => {
    const clampedZoom = Math.max(0.1, Math.min(5, zoom));
    set((state) => ({
      viewport: { ...state.viewport, zoom: clampedZoom },
    }));
  },

  setPan: (x, y) => {
    set((state) => ({
      viewport: { ...state.viewport, panX: x, panY: y },
    }));
  },

  resetView: () => {
    set({
      viewport: { ...DEFAULT_VIEWPORT_STATE },
    });
  },

  // Tools
  activeTool: 'select',
  setActiveTool: (tool) => set({ activeTool: tool }),

  // Grid & Snap
  toggleGrid: () => {
    set((state) => ({
      viewport: { ...state.viewport, showGrid: !state.viewport.showGrid },
    }));
  },

  toggleSnap: () => {
    set((state) => ({
      viewport: { ...state.viewport, snapToGrid: !state.viewport.snapToGrid },
    }));
  },

  toggleGizmos: () => {
    set((state) => ({
      viewport: { ...state.viewport, showGizmos: !state.viewport.showGizmos },
    }));
  },

  toggleRulers: () => {
    set((state) => ({
      viewport: { ...state.viewport, showRulers: !state.viewport.showRulers },
    }));
  },

  setGridSize: (size) => {
    set((state) => ({
      viewport: { ...state.viewport, gridSize: size },
    }));
  },

  // Box Selection
  boxSelection: null,

  startBoxSelection: (x, y) => {
    set({
      boxSelection: {
        isSelecting: true,
        startX: x,
        startY: y,
        endX: x,
        endY: y,
      },
    });
  },

  updateBoxSelection: (x, y) => {
    const { boxSelection } = get();
    if (boxSelection?.isSelecting) {
      set({
        boxSelection: {
          ...boxSelection,
          endX: x,
          endY: y,
        },
      });
    }
  },

  endBoxSelection: () => {
    set({ boxSelection: null });
  },

  // Mouse state
  mousePosition: { x: 0, y: 0 },
  setMousePosition: (x, y) => set({ mousePosition: { x, y } }),

  // Transform state
  isTransforming: false,
  setIsTransforming: (value) => set({ isTransforming: value }),

  // Project state methods
  loadProjectState: (state) =>
    set((current) => ({
      viewport: {
        ...current.viewport,
        zoom: state.zoom ?? 1,
        panX: state.panX ?? 0,
        panY: state.panY ?? 0,
        showGrid: state.showGrid ?? current.viewport.showGrid,
        showRulers: state.showRulers ?? current.viewport.showRulers,
        snapToGrid: state.snapToGrid ?? current.viewport.snapToGrid,
        gridSize: state.gridSize ?? current.viewport.gridSize,
      },
      activeTool: (state.activeTool as TransformTool) ?? current.activeTool,
    })),

  getProjectState: () => {
    const state = get();
    return {
      panX: state.viewport.panX ?? 0,
      panY: state.viewport.panY ?? 0,
      zoom: state.viewport.zoom ?? 1,
      activeTool: state.activeTool ?? 'select',
      showGrid: state.viewport.showGrid ?? true,
      showRulers: state.viewport.showRulers ?? false,
      snapToGrid: state.viewport.snapToGrid ?? true,
      gridSize: state.viewport.gridSize ?? 50,
    };
  },

  resetToDefaults: () =>
    set({
      viewport: { ...DEFAULT_VIEWPORT_STATE },
      activeTool: 'select',
      boxSelection: null,
      mousePosition: { x: 0, y: 0 },
      isTransforming: false,
    }),
}));
